import React, { useState, useEffect, useRef } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  TextField,
  Card,
  CardContent,
  CardActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StraightenIcon from '@mui/icons-material/Straighten';
import { getProjectScope, saveProjectScope, fetchProjectScopeFromSOW } from '../../utils/preparePhaseService';
import { updateProject } from '../../utils/api';
import { getUatTestScriptVersions } from '../../utils/uatTestScriptsService';
import SafetyConfirmDialog from '../../components/SafetyConfirmDialog';

// Styled components
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    marginRight: theme.spacing(1),
  },
}));

const ProjectScopePage = () => {
  const { projectId } = useParams();
  const { project, refreshOverview } = useOutletContext();
  const projectContextRef = React.useRef(project);
  
  const [scope, setScope] = useState({
    summary: '',
    project_type: '',
    edition: '',
    version: '',
    objectives: [],
    inScope: [],
    outOfScope: []
  });
  
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState(null);
  const [primarySource, setPrimarySource] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [fetchConfirmOpen, setFetchConfirmOpen] = useState(false);
  
  // Edit states
  const [editMode, setEditMode] = useState(false);
  const [editingScope, setEditingScope] = useState({
    summary: '',
    project_type: '',
    edition: '',
    version: '',
    objectives: [],
    inScope: [],
    outOfScope: []
  });

  const [availableScriptVersions, setAvailableScriptVersions] = useState({}); // { Private: [{label,...}], Public: [...] }
  const [versionsLoading, setVersionsLoading] = useState(false);
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [dialogItem, setDialogItem] = useState('');
  const [dialogItemIndex, setDialogItemIndex] = useState(null);
  
  // Load project scope data from database when component mounts
  useEffect(() => {
    if (projectId) {
      loadProjectScopeFromDB();
      loadAvailableScriptVersions();
    }
  }, [projectId]);

  const loadAvailableScriptVersions = async () => {
    setVersionsLoading(true);
    try {
      const data = await getUatTestScriptVersions();
      setAvailableScriptVersions(data?.editions || {});
    } catch (e) {
      setAvailableScriptVersions({});
    } finally {
      setVersionsLoading(false);
    }
  };
  
  // Load project scope from database
  const loadProjectScopeFromDB = async () => {
    setLoading(true);
    try {
      const scopeData = await getProjectScope(projectId);
      
      const projectDefaults = {
        project_type: project?.project_type || '',
        // Backward-compat: older projects stored Private/Public in `version`
        edition: project?.edition || (project?.version === 'Private' || project?.version === 'Public' ? project?.version : '') || '',
        version: project?.edition ? (project?.version || '') : (project?.version === 'Private' || project?.version === 'Public' ? '' : (project?.version || ''))
      };

      if (scopeData && scopeData.summary !== undefined) {
        // Merge project-level fields into scope data
        const merged = { ...projectDefaults, ...scopeData,
          project_type: scopeData.project_type || projectDefaults.project_type,
          edition: scopeData.edition || projectDefaults.edition,
          version: scopeData.version || projectDefaults.version
        };
        setScope(merged);
        setEditingScope(merged);
      } else {
        // No data in database, set empty state
        const emptyScope = {
          summary: '',
          objectives: [],
          inScope: [],
          outOfScope: []
        };
        setScope(emptyScope);
        setEditingScope(emptyScope);
      }
    } catch (error) {
      console.error('Error loading project scope from database:', error);
      // Set empty state on error
      const emptyScope = {
        summary: '',
        objectives: [],
        inScope: [],
        outOfScope: []
      };
      setScope(emptyScope);
      setEditingScope(emptyScope);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch scope data from project documents and save to database
  const fetchScopeFromSOW = async () => {
    if (!projectId) return;
    
    // Check if we have a SOW document
    const sowDoc = getSowDocument();
    if (!sowDoc) {
      setSnackbar({
        open: true,
        message: 'No project document found. Please upload project documents first.',
        severity: 'warning'
      });
      return;
    }
    
    setAiLoading(true);
    setAiResponse(null);
    
    try {
      // Call the new backend endpoint to fetch from project documents and save to DB
      const result = await fetchProjectScopeFromSOW(projectId);
      
      if (result && result.data) {
        // Update the scope state with the fetched and saved data
        setScope(result.data);
        setEditingScope(result.data);
        
        // Store source information
        if (result.source) {
          setPrimarySource(result.source);
        }
        
        setSnackbar({
          open: true,
          message: 'Project scope successfully fetched from project documents and saved.',
          severity: 'success'
        });
        if (refreshOverview) refreshOverview();
        
        // Automatically enter edit mode to allow user to review and modify
        setEditMode(true);
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to extract project scope from project documents.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error("Error fetching scope from project documents:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Error fetching scope from project documents.',
        severity: 'error'
      });
    } finally {
      setAiLoading(false);
    }
  };
  
  
  // Get the SOW document from the project
  const getSowDocument = () => {
    if (project && project.documents && project.documents.sow && project.documents.sow.length > 0) {
      return project.documents.sow[0];
    }
    return null;
  };
  
  
  // Enter edit mode
  const handleEdit = () => {
    setEditingScope({...scope});
    setEditMode(true);
  };
  
  // Cancel edit mode
  const handleCancel = () => {
    setEditingScope({...scope});
    setEditMode(false);
  };
  
  // Save changes to database
  const handleSave = async () => {
    setLoading(true);
    try {
      // Save scope fields (summary, objectives, in/out of scope)
      await saveProjectScope(projectId, editingScope);

      // Save project_type, edition, and version to the project table
      if (editingScope.project_type !== undefined || editingScope.edition !== undefined || editingScope.version !== undefined) {
        await updateProject(projectId, {
          project_type: editingScope.project_type,
          edition: editingScope.edition,
          version: editingScope.version
        });
      }
      
      setScope({...editingScope});
      setEditMode(false);
      if (refreshOverview) refreshOverview();
      
      setSnackbar({
        open: true,
        message: 'Project scope saved successfully.',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving project scope:', error);
      setSnackbar({
        open: true,
        message: 'Error saving project scope. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle summary change
  const handleSummaryChange = (e) => {
    setEditingScope(prevState => ({
      ...prevState,
      summary: e.target.value
    }));
  };

  // Handle dropdown field changes
  const handleDropdownChange = (field) => (e) => {
    const nextValue = e.target.value;
    setEditingScope(prevState => {
      const next = { ...prevState, [field]: nextValue };
      // If edition changes, clear version if it's not available in the new edition
      if (field === 'edition') {
        const allowed = (availableScriptVersions?.[nextValue] || []).map(v => v.label);
        if (next.version && !allowed.includes(next.version)) {
          next.version = '';
        }
      }
      return next;
    });
  };
  
  // Open add/edit dialog
  const handleOpenDialog = (type, index = null) => {
    setDialogType(type);
    setDialogItemIndex(index);
    
    if (index !== null) {
      // Edit existing item
      switch (type) {
        case 'objective':
          setDialogItem(editingScope.objectives[index]);
          break;
        case 'inScope':
          setDialogItem(editingScope.inScope[index]);
          break;
        case 'outOfScope':
          setDialogItem(editingScope.outOfScope[index]);
          break;
        default:
          setDialogItem('');
      }
    } else {
      // Add new item
      setDialogItem('');
    }
    
    setOpenDialog(true);
  };
  
  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDialogItem('');
  };
  
  // Save dialog item
  const handleSaveDialogItem = () => {
    if (!dialogItem.trim()) {
      return;
    }
    
    const updatedScope = {...editingScope};
    
    if (dialogItemIndex === null) {
      // Add new item
      switch (dialogType) {
        case 'objective':
          updatedScope.objectives = [...updatedScope.objectives, dialogItem.trim()];
          break;
        case 'inScope':
          updatedScope.inScope = [...updatedScope.inScope, dialogItem.trim()];
          break;
        case 'outOfScope':
          updatedScope.outOfScope = [...updatedScope.outOfScope, dialogItem.trim()];
          break;
        default:
          break;
      }
    } else {
      // Edit existing item
      switch (dialogType) {
        case 'objective':
          updatedScope.objectives[dialogItemIndex] = dialogItem.trim();
          break;
        case 'inScope':
          updatedScope.inScope[dialogItemIndex] = dialogItem.trim();
          break;
        case 'outOfScope':
          updatedScope.outOfScope[dialogItemIndex] = dialogItem.trim();
          break;
        default:
          break;
      }
    }
    
    setEditingScope(updatedScope);
    handleCloseDialog();
  };
  
  // Delete item
  const handleDeleteItem = (type, index) => {
    const updatedScope = {...editingScope};
    
    switch (type) {
      case 'objective':
        updatedScope.objectives = updatedScope.objectives.filter((_, i) => i !== index);
        break;
      case 'inScope':
        updatedScope.inScope = updatedScope.inScope.filter((_, i) => i !== index);
        break;
      case 'outOfScope':
        updatedScope.outOfScope = updatedScope.outOfScope.filter((_, i) => i !== index);
        break;
      default:
        break;
    }
    
    setEditingScope(updatedScope);
  };
  
  // Render a list of items with edit/delete buttons if in edit mode
  const renderList = (items, type) => {
    return (
      <List>
        {items.map((item, index) => (
          <ListItem
            key={index}
            secondaryAction={
              editMode && (
                <>
                  <IconButton edge="end" aria-label="edit" onClick={() => handleOpenDialog(type, index)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteItem(type, index)}>
                    <DeleteIcon />
                  </IconButton>
                </>
              )
            }
          >
            <ListItemIcon>
              <CheckCircleIcon color="success" />
            </ListItemIcon>
            <ListItemText primary={item} />
          </ListItem>
        ))}
        {editMode && (
          <ListItem>
            <Button
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog(type)}
              variant="outlined"
              size="small"
            >
              Add Item
            </Button>
          </ListItem>
        )}
      </List>
    );
  };

  const hasExistingScope =
    Boolean(scope.summary) ||
    scope.objectives.length > 0 ||
    scope.inScope.length > 0 ||
    scope.outOfScope.length > 0;
  
  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <SectionTitle variant="h5">
            <StraightenIcon />
            Project Scope Management
          </SectionTitle>
          <Box>
            {editMode ? (
              <>
                <Button
                  startIcon={<SaveIcon />}
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  disabled={loading}
                  sx={{ mr: 1 }}
                >
                  {loading ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  startIcon={<CancelIcon />}
                  variant="outlined"
                  color="error"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button
                  startIcon={<AutoAwesomeIcon />}
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    hasExistingScope ? setFetchConfirmOpen(true) : fetchScopeFromSOW();
                  }}
                  disabled={loading || aiLoading || !getSowDocument()}
                  sx={{ mr: 1 }}
                >
                  {loading
                    ? 'Loading...'
                    : aiLoading
                    ? hasExistingScope ? 'Regenerating...' : 'Fetching...'
                    : hasExistingScope ? 'Regenerate from Project Documents' : 'Fetch from Project Documents'}
                </Button>
                <Button
                  startIcon={<EditIcon />}
                  variant="outlined"
                  color="primary"
                  onClick={handleEdit}
                >
                  Edit
                </Button>
              </>
            )}
          </Box>
        </Box>
        
        <Typography variant="body1" paragraph>
          Define the project scope, objectives, and what is in or out of scope for this SAP implementation project.
        </Typography>
        
        {!getSowDocument() && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            No project document found. Upload project documents to enable AI-assisted content generation.
          </Alert>
        )}
        
        {primarySource && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Content generated from: {primarySource.filename || primarySource.document_name || 'Project Document'}
          </Alert>
        )}
        
        {(loading || aiLoading) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}
        
        <Grid container spacing={3}>
          {/* Project Type, Edition, and Version */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Project Type
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {editMode ? (
                  <FormControl fullWidth>
                    <InputLabel>Project Type</InputLabel>
                    <Select
                      value={editingScope.project_type || ''}
                      label="Project Type"
                      onChange={handleDropdownChange('project_type')}
                    >
                      <MenuItem value="Implementation / Development Project">Implementation / Development Project</MenuItem>
                      <MenuItem value="Support and Maintenance Project">Support and Maintenance Project</MenuItem>
                      <MenuItem value="Roll Out Project">Roll Out Project</MenuItem>
                      <MenuItem value="Upgradation Project / Migration Project">Upgradation Project / Migration Project</MenuItem>
                      <MenuItem value="Enhancement project">Enhancement project</MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  <Typography variant="body1">
                    {scope.project_type || <em style={{ color: '#888' }}>Not set</em>}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Edition
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {editMode ? (
                  <FormControl fullWidth>
                    <InputLabel>Edition</InputLabel>
                    <Select
                      value={editingScope.edition || ''}
                      label="Edition"
                      onChange={handleDropdownChange('edition')}
                    >
                      <MenuItem value="Private">Private</MenuItem>
                      <MenuItem value="Public">Public</MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  <Typography variant="body1">
                    {scope.edition || <em style={{ color: '#888' }}>Not set</em>}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Version
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {editMode ? (
                  <FormControl fullWidth disabled={versionsLoading || !editingScope.edition}>
                    <InputLabel>Version</InputLabel>
                    <Select
                      value={editingScope.version || ''}
                      label="Version"
                      onChange={handleDropdownChange('version')}
                    >
                      {(availableScriptVersions?.[editingScope.edition] || []).map((v) => (
                        <MenuItem key={v.prefix || v.label} value={v.label}>
                          {v.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <Typography variant="body1">
                    {scope.version || <em style={{ color: '#888' }}>Not set</em>}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Project Scope Summary */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Project Scope Summary
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {editMode ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    value={editingScope.summary}
                    onChange={handleSummaryChange}
                    placeholder="Enter a summary of the project scope"
                  />
                ) : (
                  <Typography variant="body1" paragraph>
                    {scope.summary}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          {/* Project Objectives */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Project Objectives
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {renderList(editMode ? editingScope.objectives : scope.objectives, 'objective')}
              </CardContent>
            </Card>
          </Grid>
          
          {/* In Scope */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="success.main">
                  In Scope
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {renderList(editMode ? editingScope.inScope : scope.inScope, 'inScope')}
              </CardContent>
            </Card>
          </Grid>
          
          {/* Out of Scope */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom color="error.main">
                  Out of Scope
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {renderList(editMode ? editingScope.outOfScope : scope.outOfScope, 'outOfScope')}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
      
      {/* AI Response Accordion (if available) */}
      {aiResponse && (
        <Accordion sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>AI-Generated Content (Raw Response)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Paper sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                {aiResponse}
              </Typography>
            </Paper>
          </AccordionDetails>
        </Accordion>
      )}
      
      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>
          {dialogItemIndex === null ? 'Add' : 'Edit'} {
            dialogType === 'objective' ? 'Objective' :
            dialogType === 'inScope' ? 'In-Scope Item' :
            'Out-of-Scope Item'
          }
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="item"
            label="Item Description"
            type="text"
            fullWidth
            multiline
            rows={5}
            variant="outlined"
            value={dialogItem}
            onChange={(e) => setDialogItem(e.target.value)}
            sx={{ minWidth: 500 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveDialogItem} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <SafetyConfirmDialog
        open={fetchConfirmOpen}
        title={hasExistingScope ? 'Regenerate Project Scope from Project Documents?' : 'Fetch Project Scope from Project Documents?'}
        warning={hasExistingScope ? 'The current Project Scope will be replaced.' : 'Project Scope will be generated from Project Documents.'}
        paragraphs={[
          hasExistingScope
            ? 'This action will use AI to regenerate the current scope summary, objectives, in-scope items, and out-of-scope items.'
            : 'This action will use AI to fetch the scope summary, objectives, in-scope items, and out-of-scope items from Project Documents.',
          ...(hasExistingScope ? ['Any manual edits made to the existing Project Scope will be permanently lost.'] : [])
        ]}
        confirmLabel={hasExistingScope ? 'Yes, Regenerate Scope' : 'Yes, Fetch Scope'}
        loading={aiLoading}
        onCancel={() => setFetchConfirmOpen(false)}
        onConfirm={() => { setFetchConfirmOpen(false); fetchScopeFromSOW(); }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProjectScopePage;
